import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Dashboard() {
  const [stats, setStats] = useState({
    total_emails: 0,
    total_campaigns: 0,
    total_lists: 0,
    recent_campaigns: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchDashboardData = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_URL}/dashboard/stats`, {
        params: { user_id: currentUser.uid }
      });

      if (response.data.success) {
        setStats(response.data.stats);
      } else {
        setError(response.data.error);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initDashboard = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          navigate('/login');
          return;
        }

        const token = await currentUser.getIdToken();
        await axios.post(`${API_URL}/auth/verify-token`, { token });

        await fetchDashboardData();
      } catch (error) {
        console.error('Dashboard initialization error:', error);
        navigate('/login');
      }
    };

    initDashboard();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const chartData = stats.recent_campaigns?.map(campaign => ({
    name: campaign.subject?.substring(0, 20) + '...' || 'Campaign',
    sent: campaign.successful_sends || 0,
    failed: campaign.failed_sends || 0,
    opened: campaign.opened || 0
  })).slice(0, 5) || [];

  if (loading) {
    return (
      <div className="flex flex-col md:flex-row h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col md:flex-row h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 p-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p>{error}</p>
            <button 
              onClick={fetchDashboardData}
              className="mt-2 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-4 rounded"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto p-4 md:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <h1 className="text-2xl font-semibold mb-4 md:mb-0">Dashboard</h1>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/upload-emails')}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              New Campaign
            </button>
            <button
              onClick={handleLogout}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
            >
              Logout
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-gray-500 text-xs font-medium uppercase tracking-wide">Total Emails Sent</h2>
            <p className="mt-1 text-3xl font-semibold text-gray-900">{stats.total_emails}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-gray-500 text-xs font-medium uppercase tracking-wide">Total Campaigns</h2>
            <p className="mt-1 text-3xl font-semibold text-gray-900">{stats.total_campaigns}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-gray-500 text-xs font-medium uppercase tracking-wide">Email Lists</h2>
            <p className="mt-1 text-3xl font-semibold text-gray-900">{stats.total_lists}</p>
          </div>
        </div>

        {chartData.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Campaign Performance</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="sent" name="Sent" fill="#3B82F6" />
                <Bar dataKey="opened" name="Opened" fill="#10B981" />
                <Bar dataKey="failed" name="Failed" fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Recent Campaigns</h2>
          </div>
          
          <div className="divide-y divide-gray-200">
            {stats.recent_campaigns && stats.recent_campaigns.length > 0 ? (
              stats.recent_campaigns.map((campaign, idx) => (
                <div key={idx} className="px-6 py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {campaign.subject || 'Untitled Campaign'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {new Date(campaign.sent_at).toLocaleDateString()} via {campaign.sender_email}
                      </p>
                    </div>
                    <div className="text-sm text-gray-500 text-right">
                      <span className="block text-green-600">
                        Sent: {campaign.successful_sends || 0}
                      </span>
                      <span className="block text-blue-600">
                        Opened: {campaign.opened || 0}
                      </span>
                      <span className="block text-red-600">
                        Failed: {campaign.failed_sends || 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-gray-500">
                <p>No campaigns yet. Start your first campaign!</p>
                <button
                  onClick={() => navigate('/upload-emails')}
                  className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  Create Campaign
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
